import React, { useState, useMemo } from 'react';
import { Bot, Mic, Upload, Image, Download, Copy, Check, Search, Sparkles, Volume2, Pause, Loader, ChevronLeft, ChevronRight, Bookmark, StickyNote } from 'lucide-react';
import { stripContextMarkers } from './messageText';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { hasTable, extractTablesFromHTML, exportAllTablesFromHTML, generateTableFilename } from '../../../../utils/chatExportUtils';
import RotatingStatusIndicator from '../../../common/RotatingStatusIndicator';
import { UNIFIED_CHAT_STATEMENTS, IMAGE_GEN_STATEMENTS } from '../../../common/statusStatements';

const MessageRenderer = ({
    message,
    audioStates,
    copiedStates,
    ttsSettings,
    onAudioToggle,
    onCopy,
    getAudioIconType,
    onSavePrompt,
    onSaveToNotes
}) => {
    const [currentPage, setCurrentPage] = useState(0);

    // Configuration for pagination
    const PAGINATION_THRESHOLD = 50000; // Characters threshold to trigger pagination
    const CHARS_PER_PAGE = 20000; // Characters per page

    // Check if message needs pagination and split content
    const paginatedContent = useMemo(() => {
        if (!message.text || message.isUser || message.text.length <= PAGINATION_THRESHOLD) {
            return { needsPagination: false, pages: [message.text], totalPages: 1 };
        }

        console.log('Paginating large message:', {
            messageLength: message.text.length,
            messageId: message.id,
            firstChars: message.text.substring(0, 200)
        });

        // Split content into pages by characters, but try to break at natural points
        const pages = [];
        let remainingText = message.text;

        while (remainingText.length > CHARS_PER_PAGE) {
            let splitPoint = CHARS_PER_PAGE;

            // Try to find a good break point (paragraph, sentence, or word boundary)
            const nearbyNewlines = remainingText.substring(splitPoint - 1000, splitPoint + 1000);
            const lastParagraph = nearbyNewlines.lastIndexOf('\n\n');
            const lastSentence = nearbyNewlines.lastIndexOf('. ');
            const lastSpace = nearbyNewlines.lastIndexOf(' ');

            if (lastParagraph > 500) {
                splitPoint = splitPoint - 1000 + lastParagraph + 2;
            } else if (lastSentence > 500) {
                splitPoint = splitPoint - 1000 + lastSentence + 2;
            } else if (lastSpace > 500) {
                splitPoint = splitPoint - 1000 + lastSpace + 1;
            }

            pages.push(remainingText.substring(0, splitPoint));
            remainingText = remainingText.substring(splitPoint);
        }

        if (remainingText.length > 0) {
            pages.push(remainingText);
        }

        console.log('Pagination complete:', {
            totalPages: pages.length,
            pageLengths: pages.map((page, idx) => ({ page: idx + 1, length: page.length, firstChars: page.substring(0, 100) }))
        });

        return { needsPagination: true, pages, totalPages: pages.length };
    }, [message.text]);

    // Format AI message text with markdown
    const formatAIMessage = (text) => {
        if (typeof text !== 'string') return '';

        // Debug logging for large responses
        if (text.length > 100000) {
            console.log('Large response detected:', {
                originalLength: text.length,
                firstChars: text.substring(0, 500),
                hasDocumentCitations: text.includes('[DOCUMENT:'),
                hasTableSyntax: text.includes('|'),
                lineCount: text.split('\n').length
            });
        }

        // Remove document citations like [DOCUMENT: filename.docx], and the space that
        // preceded them, so a stripped label does not leave "$37,856 ." behind. Same
        // helper the copy, notes and audio paths use, so what is read, pasted, saved
        // and spoken cannot drift apart again - which is how the labels reached the
        // clipboard in the first place.
        const cleanedText = stripContextMarkers(text);

        // Additional debug for cleaned text
        if (text.length > 100000) {
            console.log('After cleaning:', {
                cleanedLength: cleanedText.length,
                reductionPercent: ((text.length - cleanedText.length) / text.length * 100).toFixed(1) + '%',
                cleanedFirstChars: cleanedText.substring(0, 500)
            });
        }

        try {
            // Configure marked renderer to open links in new tab
            const renderer = new marked.Renderer();
            const originalLinkRenderer = renderer.link;
            renderer.link = function(href, title, text) {
                const html = originalLinkRenderer.call(this, href, title, text);
                return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
            };

            // Configure marked for proper formatting
            const rawHTML = marked(cleanedText, {
                gfm: true, // GitHub Flavored Markdown
                breaks: true, // Convert line breaks to <br>
                sanitize: false, // Let DOMPurify handle sanitization
                renderer: renderer
            });

            const sanitized = DOMPurify.sanitize(rawHTML, {
                ADD_ATTR: ['target', 'rel'],
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'table', 'thead', 'tbody', 'tr', 'td', 'th'],
                ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
            });

            // Debug final HTML
            if (text.length > 100000) {
                console.log('Final HTML:', {
                    htmlLength: sanitized.length,
                    htmlFirstChars: sanitized.substring(0, 500)
                });
            }

            return sanitized;
        } catch (error) {
            console.error('Error processing markdown:', error);
            // Fallback: return cleaned text with basic formatting
            return cleanedText.replace(/\n/g, '<br>');
        }
    };

    // Strip markdown and return clean plain text for copying
    const getPlainTextForCopy = (text) => {
        if (typeof text !== 'string') return '';
        
        // Remove markdown formatting and clean up text
        return text
            .replace(/\*\*\*(.+?)\*\*\*/g, '$1')      // Remove bold+italic
            .replace(/\*\*(.+?)\*\*/g, '$1')          // Remove bold
            .replace(/\*(.+?)\*/g, '$1')              // Remove italic
            .replace(/### (.+)/g, '$1')              // Remove h3
            .replace(/## (.+)/g, '$1')               // Remove h2
            .replace(/# (.+)/g, '$1')                // Remove h1
            .replace(/`(.+?)`/g, '$1')               // Remove code backticks
            .replace(/```[\s\S]*?```/g, (match) => {  // Remove code blocks but keep content
                return match.replace(/```\w*\n?/g, '').replace(/```/g, '');
            })
            .replace(/\[(.+?)\]\(.+?\)/g, '$1')      // Remove links, keep text
            .replace(/^\s*[-*+]\s+/gm, '• ')         // Convert list markers to bullets
            .replace(/^\s*\d+\.\s+/gm, '• ')         // Convert numbered lists to bullets
            .replace(/\n\s*\n\s*\n/g, '\n\n')        // Clean up multiple line breaks
            .replace(/^\s+|\s+$/g, '')               // Trim whitespace
            .trim();
    };

    // Create Google Search URL
    const createSearchUrl = (query) => `https://www.google.com/search?q=${encodeURIComponent(query)}`;

    // Get appropriate audio icon based on status
    const getAudioIcon = (messageId) => {
        const iconType = getAudioIconType(messageId);

        switch (iconType) {
            case 'loading':
                return <Loader size={16} className="animate-spin" />;
            case 'playing':
                return <Pause size={16} />;
            case 'volume':
            default:
                return <Volume2 size={16} />;
        }
    };

    // Check if current message content has tables
    const messageHasTable = useMemo(() => {
        if (message.isUser || message.isLoading || message.model === 'system') return false;
        const currentContent = paginatedContent.pages[currentPage] || message.text;
        const formattedHTML = formatAIMessage(currentContent);
        return hasTable(formattedHTML);
    }, [message, currentPage, paginatedContent]);

    // Handle table export
    const handleTableExport = () => {
        const currentContent = paginatedContent.pages[currentPage] || message.text;
        const formattedHTML = formatAIMessage(currentContent);
        const filename = generateTableFilename(message);
        exportAllTablesFromHTML(formattedHTML, filename.replace('.csv', ''));
    };

    // Get file type icon
    const getFileTypeIcon = (fileType) => {
        if (fileType && typeof fileType === 'string') {
            if (fileType.startsWith('image/')) {
                return <Image size={14} className="mr-1" />;
            } else if (fileType.startsWith('audio/')) {
                return <Mic size={14} className="mr-1" />;
            }
        }
        return <Upload size={14} className="mr-1" />;
    };

    return (
        <div className={`flex ${message.isUser ? 'justify-end' : 'justify-start'} mb-4 group/msg`} data-message-id={message.id}>
            <div className={`relative max-w-md md:max-w-lg lg:max-w-2xl p-4 rounded-lg shadow-sm font-rasa text-base ${
                message.isInterrupt ? 'bg-cyan-500 text-white ring-2 ring-cyan-300 dark:ring-cyan-600' :
                message.isUser ? 'bg-blue-500 text-white' :
                message.model === 'system' ? (message.isError ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 italic border border-red-200 dark:border-red-700' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 italic') :
                message.is_housing_related ? 'bg-blue-100 dark:bg-blue-900/30 text-gray-900 dark:text-white border border-blue-200 dark:border-blue-800' :
                'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
            }`}>
                {message.isUser ? (
                    <>
                        {/* Show interrupt label */}
                        {message.isInterrupt && (
                            <div className="text-[10px] font-semibold uppercase tracking-wider mb-1 opacity-80">
                                + Added while Jpro was thinking
                            </div>
                        )}
                        {/* Show sender name for collaborative chats */}
                        {message.sender && (
                            <div className="text-xs font-semibold mb-1 opacity-90">
                                {message.sender.username}
                            </div>
                        )}
                        <div className="whitespace-pre-wrap break-words">{message.text}</div>
                        {Array.isArray(message.files) && message.files.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {message.files.map((file, index) => {
                                    if (!file) {
                                        console.warn('Null or undefined file in message:', message.id);
                                        return null;
                                    }
                                    
                                    return (
                                        <div key={file.id || `file-${index}-${Math.random()}`} className="flex items-center p-1 bg-blue-400 rounded text-xs">
                                            {getFileTypeIcon(file.type)}
                                            <span className="truncate max-w-[100px]">{file.name || 'File'}</span>
                                            {file.url && (
                                                <a 
                                                    href={file.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="ml-2 text-white hover:text-blue-100"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <Download size={12} />
                                                </a>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {/* Save prompt / Save to Notes */}
                        {(onSavePrompt || onSaveToNotes) && (
                            <div className="flex justify-end gap-1 mt-1 -mb-1">
                                {onSaveToNotes && (
                                    <button
                                        onClick={() => onSaveToNotes(message.text)}
                                        className="p-0.5 rounded text-blue-200 hover:text-white"
                                        title="Save to Notes"
                                    >
                                        <StickyNote size={12} />
                                    </button>
                                )}
                                {onSavePrompt && (
                                    <button
                                        onClick={() => onSavePrompt(message.text)}
                                        className="p-0.5 rounded text-blue-200 hover:text-white"
                                        title="Save as prompt"
                                    >
                                        <Bookmark size={12} />
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div>
                        {message.isLoading ? (
                            <div className="py-1">
                                <RotatingStatusIndicator
                                    inline
                                    statements={message.isImageGeneration ? IMAGE_GEN_STATEMENTS : UNIFIED_CHAT_STATEMENTS}
                                />
                            </div>
                        ) : message.model === 'system' ? (
                            <div className="whitespace-pre-wrap break-words">{message.text}</div>
                        ) : (
                            <>
                                {/* Render Grounding Info HTML */}
                                {message.grounding_info && (
                                    <div className="mb-3 pb-2 border-b border-gray-200 dark:border-gray-600 text-sm">
                                        <div className="flex items-center mb-1">
                                            {/* Commented out for now */}
                                            {/* <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 flex items-center">
                                                <Search size={12} className="mr-1" /> Web Search Results
                                            </span> */}
                                        </div>
                                        {/* <div
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.grounding_info) }}
                                        /> */}
                                    </div>
                                )}

                                {/* Render Main AI Message Text with Pagination */}
                                <div>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: formatAIMessage(paginatedContent.pages[currentPage] || message.text)
                                        }}
                                        className="prose text-base prose-sm dark:prose-invert max-w-none break-words"
                                    />

                                    {/* Pagination Controls */}
                                    {paginatedContent.needsPagination && (
                                        <div className="mt-4 flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                                                    disabled={currentPage === 0}
                                                    className="p-2 rounded-lg bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors"
                                                    title="Previous page"
                                                >
                                                    <ChevronLeft size={16} />
                                                </button>

                                                <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                                                    Page {currentPage + 1} of {paginatedContent.totalPages}
                                                </span>

                                                <button
                                                    onClick={() => setCurrentPage(Math.min(paginatedContent.totalPages - 1, currentPage + 1))}
                                                    disabled={currentPage === paginatedContent.totalPages - 1}
                                                    className="p-2 rounded-lg bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors"
                                                    title="Next page"
                                                >
                                                    <ChevronRight size={16} />
                                                </button>
                                            </div>

                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {message.text.length.toLocaleString()} characters total
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Render Generated Image if present */}
                                {message.image_data && (
                                    <div className="mt-3">
                                        <img 
                                            src={`data:${message.image_data.mime_type};base64,${message.image_data.image_base64}`}
                                            alt="JPRO Generated Image"
                                            className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm"
                                        />
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            Prompt: {message.image_data.prompt_used}
                                        </p>
                                    </div>
                                )}

                                {/* Model Info and Audio Button */}
                                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
                                    <div className="flex items-center">
                                        {message.is_housing_related ? 
                                            <Bot className="w-3 h-3 mr-1 text-blue-500 flex-shrink-0" /> : 
                                            <Sparkles className="w-3 h-3 mr-1 flex-shrink-0" />
                                        }
                                        <span>
                                            {/* {message.modelName || (message.is_housing_related ? 'Housing Assistant' : 'AI Assistant')} */}
                                            JPRO
                                        </span>
                                    </div>
                                    
                                    <div className="flex items-center space-x-2">
                                        {/* Export table button - only show if message contains table */}
                                        {messageHasTable && (
                                            <button
                                                onClick={handleTableExport}
                                                className="text-gray-500 dark:text-gray-400 hover:text-green-500 dark:hover:text-green-400 flex items-center"
                                                title="Export table(s) to CSV"
                                            >
                                                <Download size={16} />
                                            </button>
                                        )}

                                        {/* Save to Notes button */}
                                        {onSaveToNotes && (
                                            <button
                                                onClick={() => onSaveToNotes(message.text)}
                                                className="text-gray-500 dark:text-gray-400 hover:text-green-500 dark:hover:text-green-400 flex items-center"
                                                title="Save to Notes"
                                            >
                                                <StickyNote size={16} />
                                            </button>
                                        )}

                                        {/* Copy button */}
                                        <button
                                            onClick={() => {
                                                // Pass original markdown text (not stripped) for formatting preservation
                                                const textToCopy = paginatedContent.needsPagination
                                                    ? paginatedContent.pages[currentPage]
                                                    : message.text;
                                                onCopy(message.id, textToCopy);
                                            }}
                                            className="text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex items-center"
                                            title={
                                                copiedStates[message.id]
                                                    ? "Copied!"
                                                    : paginatedContent.needsPagination
                                                        ? `Copy current page (${currentPage + 1}/${paginatedContent.totalPages})`
                                                        : "Copy message"
                                            }
                                        >
                                            {copiedStates[message.id] ? <Check size={16} /> : <Copy size={16} />}
                                        </button>

                                        {/* Copy full message button for paginated content */}
                                        {paginatedContent.needsPagination && (
                                            <button
                                                onClick={() => onCopy(`${message.id}_full`, message.text)}
                                                className="text-gray-500 dark:text-gray-400 hover:text-green-500 dark:hover:text-green-400 flex items-center"
                                                title={copiedStates[`${message.id}_full`] ? "Copied full message!" : "Copy full message"}
                                            >
                                                {copiedStates[`${message.id}_full`] ? <Check size={16} /> : <Copy size={16} className="opacity-75" />}
                                                <span className="text-xs ml-1">All</span>
                                            </button>
                                        )}

                                        {/* Audio button for TTS */}
                                        {ttsSettings.enabled && (
                                            <button
                                                onClick={() => onAudioToggle(message.id, message.text)}
                                                className="text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex items-center"
                                                title={
                                                    audioStates[message.id]?.status === 'loading'
                                                        ? "Loading audio..."
                                                        : audioStates[message.id]?.status === 'playing'
                                                            ? "Pause"
                                                            : "Play message"
                                                }
                                                disabled={audioStates[message.id]?.status === 'loading'}
                                            >
                                                {getAudioIcon(message.id)}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Render Search Suggestions */}
                                {/* Commented out to hide related searches from display
                                {message.search_suggestions && message.search_suggestions.length > 0 && (
                                    <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                                        <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 flex items-center">
                                             <Search size={12} className="mr-1 flex-shrink-0" />
                                            Related Searches:
                                        </p>
                                        <ul className="list-none pl-0 space-y-1">
                                            {message.search_suggestions.map((suggestion, index) => (
                                                <li key={index} className="text-xs">
                                                    <a href={createSearchUrl(suggestion)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline">
                                                        {suggestion}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                */}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageRenderer;